import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Settings,
  Users,
  Mail,
  CreditCard,
} from 'lucide-react';

type Tab = 'general' | 'members' | 'emails' | 'payouts';

const tabs: { value: Tab; label: string; icon: typeof Settings; path: string; adminOnly?: boolean }[] = [
  { value: 'general', label: 'General', icon: Settings, path: '/settings' },
  { value: 'members', label: 'Members', icon: Users, path: '/members' },
  { value: 'emails', label: 'Emails', icon: Mail, path: '/emails' },
  { value: 'payouts', label: 'Payments', icon: CreditCard, path: '/payouts', adminOnly: true },
];

export function LeagueSettingsLayout({
  activeTab,
  children,
}: {
  activeTab: Tab;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { currentMembership, isSuperAdmin } = useAuth();

  const isAdmin =
    isSuperAdmin ||
    currentMembership?.role === 'owner' ||
    currentMembership?.role === 'admin';

  const visibleTabs = tabs.filter(tab => {
    if (tab.adminOnly) return isAdmin;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      <div className="sticky top-0 z-50 pt-[env(safe-area-inset-top)] bg-gradient-to-r from-stone-800 to-stone-900">
        <header className="text-white shadow-lg">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold">
                  {currentMembership?.league?.name ?? 'League'} Settings
                </h1>
                <p className="text-sm text-stone-400">Manage your league</p>
              </div>
            </div>
          </div>
        </header>

        <div className="bg-gradient-to-br from-stone-50 to-stone-100 border-b border-stone-200">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex h-10 items-center gap-1 overflow-x-auto scrollbar-hide">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.value === activeTab;
                const handleClick = () => {
                  navigate(tab.path);
                };
                return (
                  <button
                    key={tab.value}
                    onClick={handleClick}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all gap-2 shrink-0 ${
                      isActive
                        ? 'bg-white text-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-white/50 hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 max-w-4xl">

        {children}
      </main>
    </div>
  );
}
